import { diff } from 'deep-object-diff';
import get from 'get-value';
import v4 from 'uuid/v4';
import { HDI_SG_LEVEL_L2 } from '../../../constants/form/levelConstants';
import { HDI_TICKET_PRIORITY_P3 } from '../../../constants/form/priorityConstants';
import {
    HDI_TICKET_STATUS_CLOSED,
    HDI_TICKET_STATUS_NEW,
    HDI_TICKET_STATUS_ON_HOLD,
    HDI_TICKET_STATUS_OPENED,
    HDI_TICKET_STATUS_REOPENED,
    HDI_TICKET_STATUS_SUSPENDED,
    HDI_TICKET_STATUS_WAITING_ON_OWNER
} from '../../../constants/form/statusConstants';
import {
    HDI_VIEW_TYPE_ADMIN,
    HDI_VIEW_TYPE_EDITOR,
    HDI_VIEW_TYPE_USER
} from '../../../constants/form/viewTypeConstants';
import { HDI_ROLE_EDITOR, HDI_ROLE_OWNER } from '../../../constants/roleConstants';
import { noRights, ticketRights } from '../../../constants/ticketRights';
import { getAllFormStates } from '../../../constants/Utils';
import { moduleRoles } from '../../../utils/roles';

export function isDealerAccount(data) {
    return !!(data.dealerNumber && data.dealerName);
}

export function isVisibleForCountry(userCountry) {
    return userCountry === 'UK' || userCountry === 'CY' || userCountry === 'IE' || userCountry === 'MT'
}

export function isVisibleForDepartment(solutionGroup, topic) {
    return (topic === 'FLEET - FON Terms' && solutionGroup === 'FLEET') || solutionGroup === 'MOBILIZE FINANCIAL SERVICES';
}

export function isEditorView(viewType) {
    return viewType === HDI_VIEW_TYPE_EDITOR;
}

export const listSolutionGroups = (sgData, sg) => {
    const { shortListData = [] } = sgData;
    if (shortListData.length > 0)
        return shortListData.map(item => ({ label: item.shortName, value: item.id }));
    if (sg)
        return [{ label: sg.shortName, value: sg.id }];
    return [];
};

export const listEditorSolutionGroups = (sgData, sg) => {
    const { editorSolutionGroups = [] } = sgData;
    if (editorSolutionGroups.length > 0)
        return editorSolutionGroups.map(item => ({ label: item.shortName, value: item.id }));
    if (sg)
        return [{ label: sg.shortName, value: sg.id }];
    return [];
};

export const listTransferSolutionGroups = (sgData, sg) => {
    const { transferSolutionGroups = [] } = sgData;
    if (transferSolutionGroups.length > 0)
        return transferSolutionGroups.map(item => ({ label: item.shortName, value: item.id }));
    if (sg)
        return [{ label: sg.shortName, value: sg.id }];
    return [];
};

export const getTicketRightsRole = ({ roles, ipn, dealerNumber }, {
    creatorIpn,
    solutionGroup,
    dealerNumber: dNumber
}, editorSG = []) => {
    if (moduleRoles.isEditor(roles)) {
        if (editorSG.find(item => item.id === solutionGroup.id))
            return HDI_ROLE_EDITOR;
        return undefined;
    } else if (ipn === creatorIpn || (dealerNumber && dealerNumber === dNumber)) {
        return HDI_ROLE_OWNER;
    } else {
        return undefined;
    }
};

export const getUserRole = roles => {
    return moduleRoles.isEditor(roles) ? 'EDITOR' : 'USER';
};

export const setUserRights = (type, loggedUser, ticket, editor) => {
    const userType = getTicketRightsRole(loggedUser, ticket, editor);
    return !userType ||
        (userType === HDI_ROLE_OWNER &&
            ticket.status === HDI_TICKET_STATUS_NEW) ||
        !['VIEW', 'EDIT'].includes(type)
        ? noRights
        : type === 'EDIT'
          ? ticketRights.EDIT[userType][ticket.status]
          : ticketRights.VIEW[userType];
};

export function getViewType({ roles }) {
    if (moduleRoles.isEditor(roles)) {
        return HDI_VIEW_TYPE_EDITOR;
    }
    if (moduleRoles.isAdmin(roles)) {
        return HDI_VIEW_TYPE_ADMIN;
    }
    return HDI_VIEW_TYPE_USER;
}

export function getAvailableTicketStatus(
    status,
    roles,
    level,
    isReopened,
    isVinVisible = false,
    isVinPresent = false
) {
    const isEditor = moduleRoles.isEditor(roles);
    const editor = get(roles, 'editor');

    //Status check
    if (!getAllFormStates.includes(status)) {
        return [];
    }

    if (status === HDI_TICKET_STATUS_NEW) {
        return isEditor && isVinVisible && !isVinPresent ? [
            HDI_TICKET_STATUS_WAITING_ON_OWNER
        ] : [
            HDI_TICKET_STATUS_OPENED,
            HDI_TICKET_STATUS_CLOSED
        ];
    }

    if (status === HDI_TICKET_STATUS_CLOSED) {
        return [
            HDI_TICKET_STATUS_REOPENED
        ];
    }

    if (status === HDI_TICKET_STATUS_OPENED || status === HDI_TICKET_STATUS_REOPENED) {
        const array = [
            HDI_TICKET_STATUS_CLOSED,
            isReopened ? HDI_TICKET_STATUS_REOPENED : HDI_TICKET_STATUS_OPENED
        ];
        if (isEditor) {
            if(isVinVisible && !isVinPresent) {
                return [HDI_TICKET_STATUS_CLOSED, HDI_TICKET_STATUS_WAITING_ON_OWNER];
            }
            if (editor.isSuspend && level === HDI_SG_LEVEL_L2) {
                array.push(HDI_TICKET_STATUS_SUSPENDED);
            }
            if (editor.isOnHold) {
                array.push(HDI_TICKET_STATUS_ON_HOLD);
            }
        }
        return array;
    }
    if (status === HDI_TICKET_STATUS_ON_HOLD) {
        const array = [
            isReopened ? HDI_TICKET_STATUS_REOPENED : HDI_TICKET_STATUS_OPENED
        ];
        if (isEditor) {
            if(isVinVisible && !isVinPresent) {
                return [HDI_TICKET_STATUS_WAITING_ON_OWNER];
            }
            if (editor.isSuspend && level === HDI_SG_LEVEL_L2) {
                array.push(HDI_TICKET_STATUS_SUSPENDED);
            }
            if (editor.isOnHold) {
                array.push(HDI_TICKET_STATUS_ON_HOLD);
            }
        }
        return array;
    }
    if (status === HDI_TICKET_STATUS_SUSPENDED) {
        const array = [];
        if (isEditor) {
            if(isVinVisible && !isVinPresent) {
                return [HDI_TICKET_STATUS_WAITING_ON_OWNER];
            }
            array.push(isReopened ? HDI_TICKET_STATUS_REOPENED : HDI_TICKET_STATUS_OPENED);
            if (editor.isSuspend && level === HDI_SG_LEVEL_L2) {
                array.push(HDI_TICKET_STATUS_SUSPENDED);
            }
            if (editor.isOnHold) {
                array.push(HDI_TICKET_STATUS_ON_HOLD);
            }
        }
        return array;
    }
    if (status === HDI_TICKET_STATUS_WAITING_ON_OWNER) {
        return isEditor && isVinVisible && !isVinPresent ? [
            HDI_TICKET_STATUS_CLOSED,
            HDI_TICKET_STATUS_WAITING_ON_OWNER
        ] : [
            isReopened ? HDI_TICKET_STATUS_REOPENED : HDI_TICKET_STATUS_OPENED,
            HDI_TICKET_STATUS_CLOSED
        ]
    }

    return [];
}

const setNoteStatus = (status) => {
    switch (status) {
        case HDI_TICKET_STATUS_NEW: {
            return HDI_TICKET_STATUS_OPENED;
        }
        case HDI_TICKET_STATUS_CLOSED:
        case HDI_TICKET_STATUS_OPENED:
        case HDI_TICKET_STATUS_REOPENED:
        case HDI_TICKET_STATUS_ON_HOLD:
        case HDI_TICKET_STATUS_SUSPENDED:
        case HDI_TICKET_STATUS_WAITING_ON_OWNER: {
            return '';
        }
        default: {
            return status;
        }
    }
}


export const initLoadedTicket = (ticketData) => {
    const {
        copyEmail,
        priority,
        status,
        customerAgreements,
        solutionGroup,
        templateId,
        templateName
    } = ticketData.ticket;
    const { followups, internalNotes, attachments } = ticketData;

    return {
        ticketSection: {
            ...ticketData.ticket,
            copyEmail: Array.isArray(copyEmail) ? copyEmail.map(item => ({
                label: item,
                value: item
            })) : (copyEmail && copyEmail.length > 0) ? [{ value: copyEmail, label: copyEmail }] : [],
            customerAgreements: (customerAgreements || []).map(({customerAgreement}) => ({customerAgreement: String(customerAgreement)})),
            type: templateName
        },
        templateSection: {
            solutionGroup: solutionGroup.shortName,
        },
        notesSection: {
            priority,
            status: setNoteStatus(status),
            solutionGroup: solutionGroup.id,
            subject: templateId,
        },
        followups,
        internalNotes,
        attachmentsSection: { attachments }
    };
};


export const initNewTicket = () => {
    return {
        ticketSection: {
            description: '',
            priority: HDI_TICKET_PRIORITY_P3,
            alias: '',
            copyEmail: [],
            comment: '',
            massIncident: false,
            templateId: '',
            customerAgreements: []
        }
    };
};

export const transformNewTicketForSave = (values) => {
    const data = {};

    const {
        templateId = null,
        description,
        priority,
        alias = null,
        subject,
        comment = null,
        massIncident = false,
        copyEmail = [],
        substitution = null,
        customerAgreements = [],
        scpCode = null,
        version = null,
        model = null,
        vin = null
    } = values.ticketSection;

    data.ticket = {
        description,
        priority,
        alias,
        subject,
        comment,
        massIncident,
        copyEmail: copyEmail.map(item => item.value),
        substitution,
        templateId,
        customerAgreements: customerAgreements.filter(item => !!item.customerAgreement),
        scpCode,
        version,
        model,
        vin
    };

    if (get(values, 'attachmentsSection.newAttachments', { default: false }) && values.attachmentsSection.newAttachments.length > 0) {
        data.newAttachments = [];
        values.attachmentsSection.newAttachments.forEach(attachment => {
            data.newAttachments.push({
                ...attachment,
                id: v4(),
            })
        });
    }
    return data;
};

export const transformUpdateTicketForSave = (values, sgList, subjectList) => {
    const { oldValues, newValues } = values;
    const difference = diff(oldValues, newValues);
    const data = {};
    data.ticket = {
        ...get(difference, 'ticketSection'),
        id: oldValues.ticketSection.id,
    };

    if (get(difference.ticketSection, 'copyEmail')) {
        data.ticket.copyEmail = get(newValues.ticketSection, 'copyEmail').map(item => item.value);
    }

    if(get(difference.ticketSection, 'customerAgreements')) {
        data.ticket.customerAgreements = get(newValues.ticketSection, 'customerAgreements', []).filter(item => !!item.customerAgreement);
    }

    if (get(newValues, 'notesSection.subject')) {
        data.ticket.templateId = get(newValues, 'notesSection.subject')
    }

    const note = get(newValues, 'notesSection');
    if (note) {
        const solutionGroup = findSolutionGroup(get(newValues, 'notesSection.solutionGroup'), sgList, newValues);
        data.followups = {
            ...note,
            solutionGroup
        };
        const subjectId = get(newValues, 'notesSection.subject');
        if(subjectId && subjectId !== ''){
            data.followups.subject = findSubject(subjectId, subjectList)
        } else {
            delete data.followups.subject;
        }
    }
    if (get(difference, 'attachmentsSection.newAttachments', { default: false }) && difference.attachmentsSection.newAttachments.length > 0) {
        data.newAttachments = [];
        difference.attachmentsSection.newAttachments.forEach(attachment => {
            data.newAttachments.push({
                id: v4(),
                ...attachment,
            })
        });
    }
    if (get(difference, 'internalNotesSection.text')) {
        data.internalNote = {
            uuid: v4(),
            newInternalNote: get(difference, 'internalNotesSection.text'),
        };
    }

    return data;
};

export const transformClosedTicketForReopen = (values, text) => {
    const data = {};
    const lastFollowUp = values.followups.reduce( (a, cur) => Date.parse(a.created) > Date.parse(cur.created) ? a : cur);
    data.ticket = {
        id: values.ticket.id,
        templateId: values.ticket.templateId
    };
    data.followups = {
        text,
        solutionGroup: {
            id: values.ticket.solutionGroup.id,
            shortName: values.ticket.solutionGroup.shortName
        },
        priority: values.ticket.priority,
        status: HDI_TICKET_STATUS_REOPENED,
        subject: {
            id: lastFollowUp.subject.id,
            shortName: lastFollowUp.subject.shortName
        }

    };

    return data;
};

export const findSolutionGroup = (id, list, formValues) => {
    if (!list || !list.length) {
        // dealer does not have access to the solutionGroup List cause he is never modifying it => can use current value
        const shortName = get(formValues, 'ticketSection.solutionGroup.shortName');
        const id = get(formValues, 'ticketSection.solutionGroup.id');
        return {shortName, id};
    }
    return list.find(item => item.id === id);
};
export const findSubject = (id, list) => {
    const selectedSubject = list.find(item => item.id === id);
    return {id: get(selectedSubject, "id"), shortName: get(selectedSubject, "subject")}
};

export const canShowSatisfactionPopUp = (ticketData, userDetail) => {
    const lastFollowUp = get(ticketData, 'followups', []).reduce( (a, cur) => Date.parse(a.created) > Date.parse(cur.created) ? a : cur, {});

    return (get(ticketData, 'ticket.status') === HDI_TICKET_STATUS_CLOSED &&
        getTicketRightsRole(userDetail, ticketData.ticket) === HDI_ROLE_OWNER &&
        get(userDetail, 'group') === 'UK' &&
        !get(ticketData, 'ticket.ticketSatisfactionChecked') &&
        lastFollowUp.creatorIpn !== userDetail.ipn
    );
};
